// Medical Questionnaire Frontend Validation Utilities
// Matches backend validation rules from MedicalQuestionnaireRequest.php

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface StepError {
  label: string;
  errors: Array<string | { index: number; errors: string[] }>;
}

// Custom validation rules matching backend
export class ValidationRules {
  
  /**
   * Check if text contains special characters (matches NoSpecialCharacters rule)
   * Only allows alphanumeric characters, spaces, and dashes
   */
  static hasSpecialCharacters(value: string | null): boolean {
    if (!value) return false;
    // Only allow letters, numbers, spaces, and dashes
    const allowedCharsRegex = /^[a-zA-Z0-9\s-]*$/;
    return !allowedCharsRegex.test(value);
  }

  /**
   * Check if birthdate is not in the future (matches BirthdateNotFuture rule)
   */
  static isBirthdateInFuture(birthdate: string | null): boolean {
    if (!birthdate) return false;
    const today = new Date();
    const inputDate = new Date(birthdate);
    return inputDate > today;
  }

  /**
   * Check if start date is not in the future (matches StartDateNotFuture rule)
   */
  static isStartDateInFuture(startDate: string | null): boolean {
    if (!startDate) return false;
    const today = new Date();
    const inputDate = new Date(startDate);
    return inputDate > today;
  }

  /**
   * Check if end date is after start date (matches EndDateAfterStart rule)
   */
  static isEndDateAfterStart(startDate: string | null, endDate: string | null): boolean {
    if (!startDate || !endDate) return true; // Skip validation if either is null
    const start = new Date(startDate);
    const end = new Date(endDate);
    return end >= start;
  }

  /**
   * Check if all values in an object are null or empty
   */
  static areAllValuesNull(obj: any): boolean {
    if (!obj) return true;
    return Object.values(obj).every(value => 
      value === null || 
      value === '' || 
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  /**
   * Validate required field
   */
  static isRequired(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Validate required_if condition
   */
  static isRequiredIf(value: any, condition: boolean): boolean {
    if (!condition) return true; // Not required if condition is false
    return this.isRequired(value);
  }

  /**
   * Validate numeric field with min/max
   */
  static isValidNumeric(value: any, min = 0, max = 999): boolean {
    if (value === null || value === undefined || value === '') return true; // Allow empty
    const num = Number(value);
    return !isNaN(num) && num >= min && num <= max;
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string | null): boolean {
    if (!email) return true; // Allow empty for nullable fields
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate boolean field
   */
  static isValidBoolean(value: any): boolean {
    return typeof value === 'boolean' || value === 'true' || value === 'false' || value === null;
  }
}

export class MedicalQuestionnaireValidator {
  
  /**
   * Validate personal information section
   */
  static validatePersonalInformation(data: any): ValidationResult {
    const errors: ValidationError[] = [];
    
    const requiredFields = [
      { field: 'first_name', label: 'First Name' },
      { field: 'last_name', label: 'Last Name' },
      { field: 'province', label: 'Province' },
      { field: 'city', label: 'City' },
      { field: 'barangay', label: 'Barangay' },
      { field: 'street', label: 'Street' },
      { field: 'building_number', label: 'Building Number' },
      { field: 'zip_code', label: 'Zip Code' },
      { field: 'sex', label: 'Sex' },
      { field: 'birthdate', label: 'Birthdate' },
      { field: 'cellphone_number', label: 'Cellphone Number' },
      { field: 'nationality', label: 'Nationality' },
      { field: 'number_of_clinic_visits', label: 'Number of Clinic Visits' }
    ];

    // Validate required fields
    requiredFields.forEach(({ field, label }) => {
      if (!ValidationRules.isRequired(data[field])) {
        errors.push({ field: `personal_information.${field}`, message: `${label} is required` });
      }
    });

    // Validate special character restrictions
    const noSpecialCharFields = ['first_name', 'last_name', 'middle_name', 'nationality'];
    noSpecialCharFields.forEach(field => {
      if (data[field] && ValidationRules.hasSpecialCharacters(data[field])) {
        errors.push({ field: `personal_information.${field}`, message: `${field} contains invalid characters` });
      }
    });

    // Validate birthdate
    if (data.birthdate && ValidationRules.isBirthdateInFuture(data.birthdate)) {
      errors.push({ field: 'personal_information.birthdate', message: 'Birthdate cannot be in the future' });
    }

    // Validate email if provided
    if (data.email_address && !ValidationRules.isValidEmail(data.email_address)) {
      errors.push({ field: 'personal_information.email_address', message: 'Invalid email format' });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate company health checkup section
   */
  static validateCompanyHealthCheckup(data: any): ValidationResult {
    const errors: ValidationError[] = [];
    
    const requiredFields = [
      { field: 'company_name', label: 'Company Name' },
      { field: 'company_no', label: 'Company No.' },
      { field: 'hmo_name', label: 'HMO Name' },
      { field: 'hmo_no', label: 'HMO No.' }
    ];

    // Validate required fields
    requiredFields.forEach(({ field, label }) => {
      if (!ValidationRules.isRequired(data[field])) {
        errors.push({ field: `for_company_health_checkup.${field}`, message: `${label} is required` });
      }
    });

    // Validate job category selection (at least one should be selected)
    // const jobCategories = ['rank_and_file', 'supervisory', 'managerial', 'top_management'];
    // const hasJobCategory = jobCategories.some(category => data[category] === true);
    // if (!hasJobCategory) {
    //   errors.push({ field: 'for_company_health_checkup.job_category', message: 'Please select a job category' });
    // }

    // Validate conditional field for "others" option
    if (data.test_result_option === 'others' && !ValidationRules.isRequired(data.specify_others_option)) {
      errors.push({ field: 'for_company_health_checkup.specify_others_option', message: 'Please specify the other option' });
    }

    // Validate no special characters in text fields
    const noSpecialCharFields = ['company_name', 'hmo_name', 'specify_others_option'];
    noSpecialCharFields.forEach(field => {
      if (data[field] && ValidationRules.hasSpecialCharacters(data[field])) {
        errors.push({ field: `for_company_health_checkup.${field}`, message: `${field} contains invalid characters` });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate subjective symptoms section
   */
  static validateSubjectiveSymptoms(data: any[]): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!Array.isArray(data) || data.length === 0) {
      errors.push({ field: 'subjective_symptoms', message: 'Please select at least one symptom or indicate none' });
      return { isValid: false, errors };
    }

    data.forEach((symptom, index) => {
      if (symptom.is_checked === true && !ValidationRules.isRequired(symptom.symptoms)) {
        errors.push({ 
          field: `subjective_symptoms.${index}.symptoms`, 
          message: 'Please describe the symptom' 
        });
      }

      // if (symptom.symptoms && ValidationRules.hasSpecialCharacters(symptom.symptoms)) {
      //   errors.push({ 
      //     field: `subjective_symptoms.${index}.symptoms`, 
      //     message: 'Symptom description contains invalid characters' 
      //   });
      // }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate medical history section
   */
  static validateMedicalHistory(data: any): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!data.illness_history || !Array.isArray(data.illness_history)) {
      return { isValid: true, errors }; // Allow empty medical history
    }

    data.illness_history.forEach((illness: any, index: number) => {
      if (!ValidationRules.areAllValuesNull(illness)) {
        // If any field is filled, all required fields must be filled
        if (!ValidationRules.isRequired(illness.description)) {
          errors.push({ 
            field: `medical_history.illness_history.${index}.description`, 
            message: 'Illness description is required' 
          });
        }

        if (!ValidationRules.isRequired(illness.status)) {
          errors.push({ 
            field: `medical_history.illness_history.${index}.status`, 
            message: 'Medical history status is required' 
          });
        }

        if (!illness.family_member || illness.family_member.length === 0) {
          errors.push({ 
            field: `medical_history.illness_history.${index}.family_member`, 
            message: 'Family member selection is required' 
          });
        }

        // // Validate no special characters
        // if (illness.description && ValidationRules.hasSpecialCharacters(illness.description)) {
        //   errors.push({ 
        //     field: `medical_history.illness_history.${index}.description`, 
        //     message: 'Description contains invalid characters' 
        //   });
        // }

        // if (illness.status && ValidationRules.hasSpecialCharacters(illness.status)) {
        //   errors.push({ 
        //     field: `medical_history.illness_history.${index}.status`, 
        //     message: 'Status contains invalid characters' 
        //   });
        // }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate medication history section
   */
  static validateMedicationHistory(data: any[]): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!Array.isArray(data)) {
      return { isValid: true, errors }; // Allow empty medication history
    }

    data.forEach((medication, index) => {
      if (!ValidationRules.areAllValuesNull(medication)) {
        // If medication_name is provided, other fields are required
        if (ValidationRules.isRequired(medication.medication_name)) {
          if (!ValidationRules.isRequired(medication.illness_symptom_name)) {
            errors.push({ 
              field: `medication_history.${index}.illness_symptom_name`, 
              message: 'Illness/symptom name is required' 
            });
          }

          if (!ValidationRules.isRequired(medication.intake_schedule)) {
            errors.push({ 
              field: `medication_history.${index}.intake_schedule`, 
              message: 'Intake schedule is required' 
            });
          }
        }

        // Validate no special characters
        const fields = ['medication_name', 'illness_symptom_name', 'intake_schedule'];
        fields.forEach(field => {
          if (medication[field] && ValidationRules.hasSpecialCharacters(medication[field])) {
            errors.push({ 
              field: `medication_history.${index}.${field}`, 
              message: `${field} contains invalid characters` 
            });
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate occupation section
   */
  static validateOccupation(data: any): ValidationResult {
    const errors: ValidationError[] = [];
    
    // All fields are nullable in backend, but let's validate if provided
    if (data.days_per_week !== null && !ValidationRules.isValidNumeric(data.days_per_week, 0, 999)) {
      errors.push({ field: 'occupation.days_per_week', message: 'Days per week must be between 0 and 999' });
    }

    if (data.hours_per_day !== null && !ValidationRules.isValidNumeric(data.hours_per_day, 0, 999)) {
      errors.push({ field: 'occupation.hours_per_day', message: 'Hours per day must be between 0 and 999' });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate OB/GYN history section (for females only)
   */
  static validateObGynHistory(data: any, isFemale: boolean): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!isFemale) {
      return { isValid: true, errors }; // Skip validation for males
    }

    const requiredFields = [
      { field: 'first_menstruation_age', label: 'First menstruation age' },
      { field: 'is_menopause_reached', label: 'Menopause status' },
      { field: 'is_period_regular', label: 'Period regularity' },
      { field: 'period_day_interval', label: 'Period interval' },
      { field: 'bleeding_duration_days', label: 'Bleeding duration' },
      { field: 'period_flow', label: 'Period flow' },
      { field: 'is_period_pain_experienced', label: 'Period pain' },
      { field: 'are_painkillers_used', label: 'Painkiller usage' },
      { field: 'had_sexual_intercourse', label: 'Sexual intercourse history' },
      { field: 'is_stds', label: 'STD history' },
      { field: 'is_administering_hormone', label: 'Hormone administration' },
      { field: 'is_pregnant', label: 'Pregnancy status' },
      { field: 'been_pregnant', label: 'Pregnancy history' },
      { field: 'has_child', label: 'Children status' },
      { field: 'is_breastfeeding', label: 'Breastfeeding status' },
      { field: 'is_breast_surgery', label: 'Breast surgery history' },
      { field: 'is_breast_tested', label: 'Breast testing history' },
      { field: 'had_pap_smear', label: 'Pap smear history' }
    ];

    // Validate required fields for females
    requiredFields.forEach(({ field, label }) => {
      if (!ValidationRules.isRequired(data[field])) {
        errors.push({ field: `ob_gyn_history.${field}`, message: `${label} is required` });
      }
    });

    // Conditional validations
    if (data.is_menopause_reached === 'Yes' && !ValidationRules.isValidNumeric(data.menopause_age, 0, 999)) {
      errors.push({ field: 'ob_gyn_history.menopause_age', message: 'Menopause age is required' });
    }

    if (data.are_painkillers_used === 'Yes' && !ValidationRules.isRequired(data.medication_name)) {
      errors.push({ field: 'ob_gyn_history.medication_name', message: 'Medication name is required' });
    }

    if (data.is_stds === 'Yes' && (!data.stds_type || data.stds_type.length === 0)) {
      errors.push({ field: 'ob_gyn_history.stds_type', message: 'STD type is required' });
    }

    if (data.is_administering_hormone === 'Yes' && !ValidationRules.isRequired(data.specify_hormone)) {
      errors.push({ field: 'ob_gyn_history.specify_hormone', message: 'Hormone specification is required' });
    }

    if (data.is_pregnant === 'Yes' && !ValidationRules.isValidNumeric(data.weeks_pregnant, 0, 999)) {
      errors.push({ field: 'ob_gyn_history.weeks_pregnant', message: 'Weeks pregnant is required' });
    }

    if (data.has_child === 'Yes' && (!data.children_delivery || data.children_delivery.length === 0)) {
      errors.push({ field: 'ob_gyn_history.children_delivery', message: 'Children delivery details are required' });
    }

    // Date validations
    if (data.last_period_start_date && ValidationRules.isStartDateInFuture(data.last_period_start_date)) {
      errors.push({ field: 'ob_gyn_history.last_period_start_date', message: 'Last period start date cannot be in the future' });
    }

    if (data.last_period_start_date && data.last_period_end_date && 
        !ValidationRules.isEndDateAfterStart(data.last_period_start_date, data.last_period_end_date)) {
      errors.push({ field: 'ob_gyn_history.last_period_end_date', message: 'End date must be after start date' });
    }

    if (data.last_pap_smear_date && ValidationRules.isStartDateInFuture(data.last_pap_smear_date)) {
      errors.push({ field: 'ob_gyn_history.last_pap_smear_date', message: 'Pap smear date cannot be in the future' });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate specific health assessment section
   */
  static validateSpecificHealthAssessment(data: any[]): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!Array.isArray(data)) {
      errors.push({ field: 'specific_health_assessment', message: 'Health assessment is required' });
      return { isValid: false, errors };
    }

    data.forEach((assessment, index) => {
      if (!ValidationRules.isRequired(assessment.status)) {
        errors.push({ 
          field: `specific_health_assessment.${index}.status`, 
          message: 'Please answer the question' 
        });
      }

      if (!ValidationRules.isValidNumeric(assessment.index)) {
        errors.push({ 
          field: `specific_health_assessment.${index}.index`, 
          message: 'Invalid assessment index' 
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate past surgical history section
   */
  static validatePastSurgicalHistory(data: any[]): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!Array.isArray(data)) {
      return { isValid: true, errors }; // Allow empty surgical history
    }

    data.forEach((surgery, index) => {
      if (!ValidationRules.areAllValuesNull(surgery)) {
        // If any field is filled, required fields must be filled
        if (!ValidationRules.isRequired(surgery.date)) {
          errors.push({ 
            field: `past_surgical_history.${index}.date`, 
            message: 'Surgery date is required' 
          });
        }

        if (!ValidationRules.isRequired(surgery.purpose_of_visit)) {
          errors.push({ 
            field: `past_surgical_history.${index}.purpose_of_visit`, 
            message: 'Purpose of visit is required' 
          });
        }

        // Validate date is not in future
        if (surgery.date && ValidationRules.isStartDateInFuture(surgery.date)) {
          errors.push({ 
            field: `past_surgical_history.${index}.date`, 
            message: 'Surgery date cannot be in the future' 
          });
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate vaccinations history section
   */
  static validateVaccinationsHistory(data: any[]): ValidationResult {
    const errors: ValidationError[] = [];
    
    if (!Array.isArray(data)) {
      return { isValid: true, errors }; // Allow empty vaccination history
    }

    data.forEach((vaccination, index) => {
      if (!ValidationRules.areAllValuesNull(vaccination)) {
        // If any field is filled, all fields are required
        const requiredFields = ['vaccine_type', 'brand_name', 'number_of_doses', 'date_received'];
        
        requiredFields.forEach(field => {
          if (!ValidationRules.isRequired(vaccination[field])) {
            errors.push({ 
              field: `vaccinations_history.${index}.${field}`, 
              message: `${field.replace('_', ' ')} is required` 
            });
          }
        });

        // Validate date is not in future
        if (vaccination.date_received && ValidationRules.isStartDateInFuture(vaccination.date_received)) {
          errors.push({ 
            field: `vaccinations_history.${index}.date_received`, 
            message: 'Vaccination date cannot be in the future' 
          });
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}